import React, { useEffect, useRef, useState } from 'react';

import qs from 'qs';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import useAppNavigation from '~/hooks/useAppNavigation';

import { resolveSourceUrl } from '~/services/VideoGenService';

interface CreateVideoPageProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateVideoPage({ open, onClose }: CreateVideoPageProps) {
  const { push } = useAppNavigation();
  const [videoId, setVideoId] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!videoId.trim()) {
      setPreviewUrl(null);
      setPreviewError(null);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewUrl(null);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await resolveSourceUrl(videoId.trim());
        setPreviewUrl(response.result.url);
      } catch {
        setPreviewError('Could not resolve video. Check the ID and try again.');
      } finally {
        setPreviewLoading(false);
      }
    }, 600);

    return () => clearTimeout(debounceRef.current);
  }, [videoId]);

  const handleNext = () => {
    onClose();
    const search = qs.stringify({ videoId: videoId.trim() });
    push(`/marketing/video-gen/edit?${search}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Create New Video</Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            value={videoId}
            onChange={(e) => setVideoId(e.target.value)}
            label="Video ID"
            fullWidth
            placeholder="Asset UUID or Vimeo ID"
            helperText="Enter an uploaded asset ID or a Vimeo video ID"
          />

          <Box
            sx={{
              width: '100%',
              minHeight: 200,
              borderRadius: 1,
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {!videoId.trim() && (
              <Typography variant="body2" color="text.secondary">
                Enter a video ID above to preview
              </Typography>
            )}

            {videoId.trim() && previewLoading && (
              <Stack alignItems="center" spacing={1}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading preview...
                </Typography>
              </Stack>
            )}

            {videoId.trim() && previewError && (
              <Typography variant="body2" color="error">
                {previewError}
              </Typography>
            )}

            {previewUrl && (
              <video
                src={previewUrl}
                controls
                muted
                playsInline
                style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={!previewUrl}>
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}
